import type { MouseEvent, PointerEvent } from 'react';
import EFPicture from 'elem/compCommon/EFPicture';
import css from './index.module.scss';
import { useGlobal } from '@/utils/useGlobal';

const NavArrow = ({ direction }: { direction: 'prev' | 'next' }) => (
    <svg
        className="oc-slider__nav-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 28 28"
        fill="none"
        aria-hidden="true"
    >
        <path
            d={direction === 'prev' ? 'M17.5 21L10.5 14L17.5 7' : 'M10.5 7L17.5 14L10.5 21'}
            stroke="currentColor"
            strokeWidth="2.33333"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export default function N1() {
    const { useLang, Link, useRouter, useState, useEffect, useRef } = useGlobal();
    const { locale } = useRouter();
    const { t } = useLang('outdoorrecreation');
    const trackRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef({
        active: false,
        moved: false,
        startX: 0,
        scrollLeft: 0,
    });
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(true);
    const LOCAL_SITES: string[] = ['za', 'es', 'it', 'uk', 'au'];
    const EU_SITES: string[] = ['de', 'fr', 'eu', 'cis'];

    const card = (img: string, textKey: string, linkKey: string) => ({
        img,
        desc: t(textKey),
        link: t(linkKey),
    });

    const img = (n: number) => `/responsive/picture/outdoor-recreation/13_${n}`;

    const lists: any = {
        de: [1, 2, 3, 4, 5, 6].map((n) => card(img(n), `secN_card${n}_text`, `secN_card${n}_link`)),
        fr: [1, 2, 3, 4, 5, 6].map((n) => card(img(n), `secN_card${n}_text`, `secN_card${n}_link`)),
        it: [1, 2, 3, 4, 5, 6, 7].map((n) => card(img(n), `secN_card${n}_text`, `secN_card${n}_link`)),
        es: [1, 2, 3, 4, 5, 6, 7].map((n) => card(img(n), `secN_card${n}_text`, `secN_card${n}_link`)),
        uk: [
            ...[1, 2, 3, 4, 5].map((n) => card(img(n), `secN_tab1_card${n}_text`, `secN_tab1_card${n}_link`)),
            ...[1, 2, 3].map((n) => card(img(n + 5), `secN_tab2_card${n}_text`, `secN_tab2_card${n}_link`)),
            ...[1, 2].map((n) => card(img(n + 8), `secN_tab3_card${n}_text`, `secN_tab3_card${n}_link`)),
        ],
    };

    const items = lists[locale] || lists.de;
    const imgSuffix = LOCAL_SITES.includes(locale) ? `_${locale}` : EU_SITES.includes(locale) ? `_de` : '';

    const updateNav = () => {
        const el = trackRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setCanPrev(scrollLeft > 1);
        setCanNext(scrollLeft + clientWidth < scrollWidth - 1);
    };

    const scrollByDir = (dir: -1 | 1) => {
        const el = trackRef.current;
        if (!el) return;
        const item = el.querySelector('.oc-slider__item') as HTMLElement | null;
        const gap = parseFloat(getComputedStyle(el).columnGap || getComputedStyle(el).gap) || 12;
        const step = (item?.offsetWidth || 600) + gap;
        el.scrollBy({ left: dir * step, behavior: 'smooth' });
    };

    const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
        if (e.pointerType === 'touch') return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;

        const el = trackRef.current;
        if (!el) return;

        dragRef.current = {
            active: true,
            moved: false,
            startX: e.clientX,
            scrollLeft: el.scrollLeft,
        };
        el.setPointerCapture?.(e.pointerId);
        el.classList.add('is-dragging');
    };

    const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        const el = trackRef.current;
        if (!drag.active || !el) return;

        const dx = e.clientX - drag.startX;
        if (Math.abs(dx) > 5) {
            drag.moved = true;
        }
        el.scrollLeft = drag.scrollLeft - dx;
    };

    const handlePointerUp = (e: PointerEvent<HTMLDivElement>) => {
        const el = trackRef.current;
        if (!dragRef.current.active) return;

        dragRef.current.active = false;
        el?.releasePointerCapture?.(e.pointerId);
        el?.classList.remove('is-dragging');
    };

    const handleClickCapture = (e: MouseEvent<HTMLDivElement>) => {
        if (dragRef.current.moved) {
            e.preventDefault();
            e.stopPropagation();
            dragRef.current.moved = false;
        }
    };

    useEffect(() => {
        const el = trackRef.current;
        if (!el) return;

        updateNav();
        el.addEventListener('scroll', updateNav, { passive: true });
        window.addEventListener('resize', updateNav);

        return () => {
            el.removeEventListener('scroll', updateNav);
            window.removeEventListener('resize', updateNav);
        };
    }, [items.length]);

    return (
        <div id="outdoor-recreation-N" className={`outdoor-recreation-N1 ${css['outdoor-recreation-N1']}`}>
            <div className="oc-slider">
                <div className="oc-slider__content">
                    <div className="oc-slider__header">
                        <p className="oc-slider__title">{t('secN_title')}</p>
                    </div>
                    <div
                        className="oc-slider__track"
                        ref={trackRef}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={handlePointerUp}
                        onClickCapture={handleClickCapture}
                    >
                        {items?.map((item: any, k: number) => (
                            <div className={`oc-slider__item oc-slider__item--${k + 1}`} key={k}>
                                <EFPicture className="oc-slider__img" src={`${item?.img}${imgSuffix}.jpg`} alt={item.desc} />
                                <div className="oc-slider__body">
                                    <p className="oc-slider__desc">{item?.desc}</p>
                                    <Link className="oc-slider__link" href={item?.link}>
                                        <span className="oc-slider__link-text">{t('common_card_more')}</span>
                                        <svg
                                            className="oc-slider__link-icon"
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                        >
                                            <path
                                                d="M5.99219 14.542L12.2573 10.0552L5.99219 5.56787L7.15674 3.94189L14.5571 9.24219C14.8192 9.42999 14.9746 9.73278 14.9746 10.0552C14.9745 10.3776 14.8192 10.6804 14.5571 10.8682L7.15674 16.168L5.99219 14.542Z"
                                                fill="black"
                                            />
                                        </svg>
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="oc-slider__nav">
                        <button
                            type="button"
                            className={`oc-slider__nav-btn oc-slider__nav-btn--prev${!canPrev ? ' is-disabled' : ''}`}
                            aria-label="Previous"
                            disabled={!canPrev}
                            onClick={() => scrollByDir(-1)}
                        >
                            <NavArrow direction="prev" />
                        </button>
                        <button
                            type="button"
                            className={`oc-slider__nav-btn oc-slider__nav-btn--next${!canNext ? ' is-disabled' : ''}`}
                            aria-label="Next"
                            disabled={!canNext}
                            onClick={() => scrollByDir(1)}
                        >
                            <NavArrow direction="next" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
